import React from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExclamationTriangle } from 'react-bootstrap-icons';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Paper
} from "@mui/material";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ 
      p: 3.5, 
      background: "#f0f4ff", 
      minHeight: "100vh",
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          404 - Page Not Found
        </Typography>
      </Breadcrumbs>

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          minHeight: 'calc(100vh - 120px)'
        }}
      >
        <Paper
          sx={{
            background: "#fff",
            borderRadius: "14px",
            border: "1px solid #e4eaf8",
            boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
            maxWidth: '600px',
            width: '100%',
            p: 6,
            textAlign: 'center'
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "20px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: '0 auto 24px',
              boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
            }}
          >
            <ExclamationTriangle size={40} color="#fff" />
          </Box>

          {/* 404 Text */}
          <Typography
            variant="h1"
            sx={{
              fontSize: '6rem',
              fontWeight: 800,
              background: primaryGradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1,
              mb: 2
            }}
          >
            404
          </Typography>

          {/* Subheading */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1a237e",
              mb: 1.5
            }}
          >
            Oops! Page Not Found
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              color: "#7b8ab8",
              mb: 4,
              maxWidth: '400px',
              mx: 'auto'
            }}
          >
            The page you're looking for doesn't exist or has been moved.
          </Typography>

          {/* Button */}
          <Button
            onClick={() => navigate('/dashboard')}
            className="d-flex align-items-center justify-content-center gap-2 fw-bold mx-auto"
            style={{
              background: primaryGradient,
              border: "none",
              borderRadius: "10px",
              padding: "12px 30px",
              boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
              color: '#fff',
              fontSize: '0.95rem'
            }}
          >
            <ArrowLeft size={16} />
            Go Back to Dashboard
          </Button>

          {/* Optional: Helpful Links */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e4eaf8' }}>
            <Typography variant="caption" sx={{ color: "#7b8ab8", display: 'block', mb: 1.5 }}>
              You might want to visit:
            </Typography>
            <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
              {[
                { label: 'Dashboard', path: '/dashboard' },
                { label: 'Users', path: '/users' },
                { label: 'Classes', path: '/data/classses' },
                { label: 'Support', path: '/support/tickets' }
              ].map((link) => (
                <Link
                  key={link.label}
                  href={link.path}
                  underline="hover"
                  sx={{
                    color: '#0d47a1',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#1a237e'
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(link.path);
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default NotFound;